/**
 * Сторож гидрации: спасает вкладку, застрявшую пустой оболочкой.
 *
 * Наблюдаемая поломка (прогон 29.08.2026): браузер запрашивает чанки роутов и получает по ним
 * `net::ERR_FAILED` — не 404, а обрыв. Тогда не срабатывает ничего из существующей защиты:
 * `vite:preloadError` спрашивает сервер про файл и на сетевой ошибке молчит, границы ошибок нет,
 * потому что React до неё не дошёл, а `unhandledrejection` про гидрацию ничего не знает.
 * Человек остаётся на странице, где нарисован только хедер: ни формы, ни ошибки, ни подсказки.
 * Те же адреса в свежей вкладке открывались нормально — сбой разовый, и перезагрузка его лечит.
 *
 * Поэтому здесь отдельная защёлка, а не `requestChunkReload`: тот перезагружает только когда
 * сервер отдаёт ДРУГУЮ сборку (защита от петли при протухшем релизе), а тут сборка та же —
 * упала сеть. Петлю закрывает отметка в `sessionStorage`: одна попытка на вкладку.
 */

export const HYDRATION_RELOAD_KEY = 'chunk-error:hydration-reloaded-at';

/** Сколько ждём гидрацию, прежде чем решить, что вкладка застряла. */
export const HYDRATION_TIMEOUT_MS = 8000;

/** Повторная попытка в той же вкладке — только через это время. */
export const HYDRATION_RELOAD_COOLDOWN_MS = 10 * 60_000;

type Storage = Pick<globalThis.Storage, 'getItem' | 'setItem'>;

export type WatchdogOutcome =
  | 'hydrated'
  | 'no-failures'
  | 'offline'
  | 'native'
  | 'cooldown'
  | 'reloaded';

type WatchdogInput = {
  storage: Storage;
  reload: () => void;
  /** Ставится приложением, когда дерево смонтировалось. */
  isHydrated: () => boolean;
  /** Сколько ассетов не загрузилось к моменту проверки. */
  failedAssets: () => number;
  isOnline?: () => boolean;
  isNative?: boolean;
  now?: () => number;
  timeoutMs?: number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  report?: (outcome: WatchdogOutcome) => void;
};

const canReload = (storage: Storage, now: number) => {
  try {
    const raw = storage.getItem(HYDRATION_RELOAD_KEY);

    if (!raw) {
      return true;
    }

    const last = Number(raw);

    return !Number.isFinite(last) || now - last > HYDRATION_RELOAD_COOLDOWN_MS;
  } catch {
    // Хранилище закрыто. Одна перезагрузка всё равно лучше, чем пустая страница навсегда:
    // после неё гидрация либо пройдёт, либо человек увидит нормальный экран ошибки.
    return true;
  }
};

/** Решение сторожа. Вынесено отдельно от таймера, чтобы проверяться без ожидания. */
export const decideHydrationReload = ({
  storage,
  reload,
  isHydrated,
  failedAssets,
  isOnline = () => navigator.onLine,
  isNative = false,
  now = Date.now,
  report,
}: Omit<WatchdogInput, 'timeoutMs' | 'setTimer'>): WatchdogOutcome => {
  const finish = (outcome: WatchdogOutcome) => {
    report?.(outcome);

    return outcome;
  };

  if (isHydrated()) {
    return finish('hydrated');
  }

  // Без единого упавшего ассета «не гидрировались» значит что-то другое — например, человек
  // открыл вкладку в фоне и браузер отложил работу. Перезагрузка тут только помешает.
  if (failedAssets() === 0) {
    return finish('no-failures');
  }

  // Оффлайн перезагружать бессмысленно: получим ту же пустую страницу, только без кеша.
  if (!isOnline()) {
    return finish('offline');
  }

  // В приложении чанки лежат в бандле, а reload() значит холодный старт вебвью.
  if (isNative) {
    return finish('native');
  }

  const at = now();

  if (!canReload(storage, at)) {
    return finish('cooldown');
  }

  try {
    storage.setItem(HYDRATION_RELOAD_KEY, String(at));
  } catch {
    // Не записалось — переживём: вторую попытку остановит уже сама успешная гидрация.
  }

  reload();

  return finish('reloaded');
};

/** Заводит таймер проверки. Возвращает функцию отмены. */
export const startHydrationWatchdog = ({
  timeoutMs = HYDRATION_TIMEOUT_MS,
  setTimer = setTimeout,
  ...rest
}: WatchdogInput) => {
  const timer = setTimer(() => decideHydrationReload(rest), timeoutMs);

  return () => clearTimeout(timer as ReturnType<typeof setTimeout>);
};
